// client/src/components/ui/CalendarPicker.jsx
import { useState, useRef, useEffect } from 'react'
import { ChevronLeft, ChevronRight, Calendar, X } from 'lucide-react'
import clsx from 'clsx'

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
]
const DAYS = ['Mo','Tu','We','Th','Fr','Sa','Su']

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth()    === b.getMonth()    &&
    a.getDate()     === b.getDate()
}

function toDateStr(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function parseLocalDate(str) {
  // Parse "YYYY-MM-DD" without timezone shift
  if (!str) return null
  const [y, m, d] = str.split('-').map(Number)
  return new Date(y, m - 1, d)
}

export default function CalendarPicker({
  value,
  onChange,
  minDate,
  placeholder = 'Select a date',
  className = '',
}) {
  const today      = new Date(); today.setHours(0, 0, 0, 0)
  const minDay     = minDate ? (minDate instanceof Date ? minDate : new Date(minDate)) : null
  if (minDay) minDay.setHours(0, 0, 0, 0)

  const selectedDate = parseLocalDate(value)

  const initYear  = selectedDate?.getFullYear()  ?? today.getFullYear()
  const initMonth = selectedDate?.getMonth()      ?? today.getMonth()

  const [open,       setOpen]      = useState(false)
  const [viewYear,   setViewYear]  = useState(initYear)
  const [viewMonth,  setViewMonth] = useState(initMonth)
  const containerRef = useRef(null)

  // Sync view when value changes externally
  useEffect(() => {
    if (selectedDate) {
      setViewYear(selectedDate.getFullYear())
      setViewMonth(selectedDate.getMonth())
    }
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Build day grid
  const firstOfMonth = new Date(viewYear, viewMonth, 1)
  const startDow     = (firstOfMonth.getDay() + 6) % 7 // Monday = 0
  const daysInMonth  = new Date(viewYear, viewMonth + 1, 0).getDate()
  const cells = [
    ...Array(startDow).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(viewYear, viewMonth, i + 1)),
  ]

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1) }
    else setViewMonth(m => m - 1)
  }
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1) }
    else setViewMonth(m => m + 1)
  }

  const handleSelect = (date) => {
    onChange(toDateStr(date))
    setOpen(false)
  }

  const handleClear = (e) => {
    e.stopPropagation()
    onChange('')
  }

  const displayLabel = selectedDate
    ? selectedDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : placeholder

  return (
    <div ref={containerRef} className={clsx('relative', className)}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={clsx(
          'w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors',
          'bg-[var(--bg-base)] border focus:outline-none',
          open
            ? 'border-indigo-500'
            : 'border-[var(--border)] hover:border-[var(--border-strong)]',
          selectedDate ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]'
        )}
      >
        <Calendar size={14} className="shrink-0 text-[var(--text-muted)]" />
        <span className="flex-1 text-left">{displayLabel}</span>
        {selectedDate && (
          <span
            role="button"
            tabIndex={0}
            onClick={handleClear}
            onKeyDown={e => e.key === 'Enter' && handleClear(e)}
            className="shrink-0 p-0.5 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)]
                       hover:bg-[var(--bg-overlay)] transition-colors"
          >
            <X size={12} />
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          className="absolute z-50 top-full left-0 mt-1.5 w-72
                     bg-[var(--bg-surface)] border border-[var(--border)]
                     rounded-xl shadow-xl p-3 animate-scale-in"
        >
          {/* Month / year nav */}
          <div className="flex items-center justify-between mb-3 px-1">
            <button
              type="button"
              onClick={prevMonth}
              className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)]
                         hover:bg-[var(--bg-overlay)] transition-colors"
            >
              <ChevronLeft size={15} />
            </button>
            <p className="text-sm font-semibold text-[var(--text-primary)] select-none">
              {MONTHS[viewMonth]} {viewYear}
            </p>
            <button
              type="button"
              onClick={nextMonth}
              className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)]
                         hover:bg-[var(--bg-overlay)] transition-colors"
            >
              <ChevronRight size={15} />
            </button>
          </div>

          {/* Weekday labels */}
          <div className="grid grid-cols-7 mb-1">
            {DAYS.map(d => (
              <div key={d}
                className="flex items-center justify-center h-7
                           text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                {d}
              </div>
            ))}
          </div>

          {/* Day buttons */}
          <div className="grid grid-cols-7 gap-y-0.5">
            {cells.map((date, i) => {
              if (!date) return <div key={`_${i}`} />

              const isSelected = selectedDate && isSameDay(date, selectedDate)
              const isToday    = isSameDay(date, today)
              const isDisabled = minDay && date < minDay

              return (
                <button
                  key={date.getTime()}
                  type="button"
                  onClick={() => !isDisabled && handleSelect(date)}
                  disabled={isDisabled}
                  className={clsx(
                    'flex items-center justify-center h-8 w-full rounded-lg',
                    'text-xs font-medium transition-colors select-none',
                    isSelected
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : isToday && !isDisabled
                        ? 'text-indigo-400 ring-1 ring-indigo-500/40 hover:bg-indigo-600/10'
                        : isDisabled
                          ? 'text-[var(--text-muted)] opacity-25 cursor-not-allowed'
                          : 'text-[var(--text-secondary)] hover:bg-[var(--bg-overlay)] hover:text-[var(--text-primary)]'
                  )}
                >
                  {date.getDate()}
                </button>
              )
            })}
          </div>

          {/* Today shortcut */}
          {(!minDay || today >= minDay) && (
            <div className="mt-2 pt-2 border-t border-[var(--border)]">
              <button
                type="button"
                onClick={() => handleSelect(today)}
                className="w-full py-1.5 text-xs font-medium rounded-lg transition-colors
                           text-indigo-400 hover:text-indigo-300 hover:bg-indigo-600/10"
              >
                Today
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
